import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "../lib/api";
import CustomLeadForm from "../components/CustomLeadForm";
import HomepageSocialProof from "../components/HomepageSocialProof";
import SEO from "../components/SEO";

/**
 * Weddinsite · Dark Elegant Landing.
 * Premium black + gold theme. Replaces the previous beige landing.
 *
 * Sections: SEO Hero → Live counters → How it works → Features → Templates
 * → Pricing (Free vs ₹999) → Testimonials → Custom design CTA → Footer.
 */

const GOLD = "#D4AF37";
const GOLD_SOFT = "#F4E5BC";
const INK = "#0A0908";
const INK_2 = "#15120E";
const LINE = "#2a2620";

function NavbarDark() {
  return (
    <nav className="fixed top-0 inset-x-0 z-40 px-6 md:px-10 py-4 flex items-center justify-between" style={{ background: "rgba(10,9,8,0.75)", backdropFilter: "blur(12px)", borderBottom: `1px solid ${LINE}` }}>
      <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
        <span className="font-serif-display text-xl tracking-wide" style={{ color: GOLD_SOFT }}>Weddinsite</span>
        <span className="text-[9px] uppercase tracking-[0.4em] hidden md:inline" style={{ color: GOLD }}>· Est. 2026</span>
      </Link>
      <div className="flex items-center gap-3 md:gap-6">
        <Link to="/templates" className="text-xs uppercase tracking-[0.3em] hover:opacity-100 transition-opacity hidden sm:inline" style={{ color: GOLD_SOFT, opacity: 0.75 }}>Templates</Link>
        <Link to="/login" className="text-xs uppercase tracking-[0.3em] hover:opacity-100 transition-opacity" style={{ color: GOLD_SOFT, opacity: 0.75 }}>Sign in</Link>
        <Link to="/signup" data-testid="nav-cta-start" className="text-xs uppercase tracking-[0.25em] px-4 py-2.5 border" style={{ background: GOLD, color: INK, borderColor: GOLD }}>
          Start Free
        </Link>
      </div>
    </nav>
  );
}

function useCountUp(target, duration = 1500) {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!target) return;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setV(Math.floor(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick); else setV(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return v;
}

export default function Landing() {
  const [proof, setProof] = useState({ couples_total: 0, published_this_week: 0, rsvps_total: 0 });
  const [templates, setTemplates] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [cfg, setCfg] = useState({ support_whatsapp: "919936120357", support_phone: "9936120357" });

  useEffect(() => {
    api.get("/config/social-proof").then((r) => setProof(r.data || {})).catch(() => {});
    api.get("/templates").then((r) => setTemplates(r.data || [])).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data || [])).catch(() => {});
    api.get("/config/pricing").then((r) => setCfg(r.data || {})).catch(() => {});
  }, []);

  const couples = useCountUp(Math.max(proof.couples_total, 134));
  const weekly = useCountUp(Math.max(proof.published_this_week, 18));
  const rsvps = useCountUp(Math.max(proof.rsvps_total, 2840));

  return (
    <div className="min-h-screen" style={{ background: INK, color: GOLD_SOFT, fontFamily: "Georgia, 'Playfair Display', serif" }} data-testid="landing-dark">
      <SEO
        title="Free Wedding Website Builder India"
        description="Build your wedding invitation website free. 23 cinematic templates, live RSVP tracker, guest management. No credit card. Pay only when you go live."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Weddinsite",
          "url": "https://weddinsite.com",
          "logo": "https://weddinsite.com/favicon.ico",
          "description": "India's premier cinematic wedding invitation website builder.",
          "sameAs": ["https://www.instagram.com/weddinsite"],
        }}
      />
      <NavbarDark />

      {/* HERO */}
      <section className="relative pt-32 pb-24 md:pt-44 md:pb-32 px-6 md:px-10 overflow-hidden text-center" data-testid="hero-section">
        {/* Glow halo */}
        <div className="absolute pointer-events-none inset-0 flex items-center justify-center">
          <div style={{ width: "min(1100px, 90vw)", height: "min(1100px, 90vw)", background: `radial-gradient(circle at 50% 50%, ${GOLD}1f 0%, ${GOLD}0a 28%, transparent 60%)` }} />
        </div>
        <div className="relative max-w-5xl mx-auto">
          <div className="text-[10px] uppercase tracking-[0.5em] mb-6" style={{ color: GOLD }}>Weddinsite · India&apos;s Cinematic Wedding Studio</div>
          <h1 className="font-serif-display font-light leading-[0.95] tracking-tight" style={{ fontSize: "clamp(40px, 7.5vw, 96px)" }} data-testid="hero-title">
            Your Wedding Website. <em className="not-italic" style={{ color: GOLD }}>Free.</em> Forever.
          </h1>
          <p className="mt-7 text-lg md:text-2xl leading-relaxed max-w-3xl mx-auto" style={{ color: "#a8a098" }}>
            <span className="font-semibold" style={{ color: GOLD_SOFT }}>FREE for life to build & preview.</span>
            <span className="hidden md:inline"> ·</span><br className="md:hidden" />
            No credit card. No subscription. No expiry.
          </p>

          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <Link to="/templates" data-testid="hero-start-free" className="inline-flex items-center gap-2 px-8 py-4 text-sm uppercase tracking-[0.3em] font-semibold transition-transform hover:scale-[1.02]" style={{ background: GOLD, color: INK }}>
              Start Building — FREE →
            </Link>
            <Link to="/demo" data-testid="hero-live-demo" className="inline-flex items-center gap-2 px-8 py-4 text-sm uppercase tracking-[0.3em] border transition-colors hover:bg-white/5" style={{ borderColor: GOLD, color: GOLD_SOFT }}>
              See 23 Live Demos →
            </Link>
          </div>

          <div className="mt-6 text-[10px] uppercase tracking-[0.4em]" style={{ color: "#807870" }}>
            ★ No card needed · Pay later only when you decide to share with guests
          </div>

          <div className="mt-12 inline-flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-[11px] uppercase tracking-[0.3em]" style={{ color: "#a8a098" }}>
            <span>✓ 23 Cinematic Templates</span>
            <span>✓ Unlimited Guests</span>
            <span>✓ RSVP Tracker</span>
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="py-10 md:py-12 px-6" style={{ borderTop: `1px solid ${LINE}`, borderBottom: `1px solid ${LINE}`, background: INK_2 }} data-testid="proof-strip">
        <div className="max-w-5xl mx-auto grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="font-serif-display text-3xl md:text-5xl tabular-nums" style={{ color: GOLD_SOFT }}>{couples.toLocaleString("en-IN")}+</div>
            <div className="text-[10px] uppercase tracking-[0.3em] mt-2" style={{ color: "#807870" }}>Couples on Weddinsite</div>
          </div>
          <div style={{ borderLeft: `1px solid ${LINE}`, borderRight: `1px solid ${LINE}` }}>
            <div className="font-serif-display text-3xl md:text-5xl tabular-nums inline-flex items-center gap-2" style={{ color: GOLD_SOFT }}>
              <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: "#4ade80" }} /> {weekly}
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] mt-2" style={{ color: "#807870" }}>Published this week</div>
          </div>
          <div>
            <div className="font-serif-display text-3xl md:text-5xl tabular-nums" style={{ color: GOLD_SOFT }}>{rsvps.toLocaleString("en-IN")}</div>
            <div className="text-[10px] uppercase tracking-[0.3em] mt-2" style={{ color: "#807870" }}>RSVPs collected</div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 md:py-28 px-6 md:px-10" data-testid="how-it-works">
        <div className="max-w-6xl mx-auto">
          <div className="text-[10px] uppercase tracking-[0.5em] text-center mb-4" style={{ color: GOLD }}>How it works</div>
          <h2 className="font-serif-display text-4xl md:text-6xl font-light text-center max-w-3xl mx-auto mb-16">From idea to invitation — in <em style={{ color: GOLD }}>three quiet steps.</em></h2>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Build free in 5 minutes", d: "Sign up, pick a template, fill in your story. No card needed." },
              { n: "02", t: "Preview & customize", d: "Edit names, photos, events, RSVP — share a private preview with family." },
              { n: "03", t: "Share when ready", d: "When you love it, share with the world. One-time fee at publish — no monthly bills, no renewals." },
            ].map((s, i) => (
              <div key={i} className="p-8 transition-all hover:translate-y-[-4px]" style={{ background: INK_2, border: `1px solid ${LINE}` }} data-testid={`step-${i+1}`}>
                <div className="font-serif-display text-7xl font-light" style={{ color: GOLD }}>{s.n}</div>
                <div className="font-serif-display text-2xl md:text-3xl mt-2" style={{ color: GOLD_SOFT }}>{s.t}</div>
                <p className="mt-3 leading-relaxed" style={{ color: "#a8a098" }}>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 md:py-28 px-6 md:px-10" style={{ background: INK_2, borderTop: `1px solid ${LINE}`, borderBottom: `1px solid ${LINE}` }} data-testid="features-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-[10px] uppercase tracking-[0.5em] mb-4" style={{ color: GOLD }}>What&apos;s included</div>
          <h2 className="font-serif-display text-4xl md:text-6xl font-light max-w-3xl mb-14">Everything for your perfect wedding website.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px" style={{ background: LINE }}>
            {[
              { i: "🎬", t: "Cinematic Templates", d: "23 designer themes with intros, animations, and music." },
              { i: "♥", t: "RSVP Tracking", d: "Live tracker — attending, maybe, declined — guest by guest." },
              { i: "💬", t: "Easy Share", d: "Personalised messages, QR codes & email auto-reminders." },
              { i: "👰", t: "Guest Management", d: "Add guests in bulk, send invites, view individual statuses." },
              { i: "📅", t: "All Events", d: "Haldi, Mehendi, Sangeet, Ceremony, Reception — schedule, venue, photos." },
              { i: "📸", t: "Gallery & Story", d: "Story timeline, hero video, photo gallery, guestbook with photo wall." },
            ].map((f, i) => (
              <div key={i} className="p-7" style={{ background: INK }} data-testid={`feature-${i}`}>
                <div className="text-3xl mb-3">{f.i}</div>
                <div className="font-serif-display text-xl mt-1" style={{ color: GOLD_SOFT }}>{f.t}</div>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "#a8a098" }}>{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TEMPLATES STRIP */}
      <HomepageSocialProof />

      <section className="py-20 md:py-28 px-6 md:px-10" data-testid="templates-strip">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-end justify-between gap-4 mb-10 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.5em] mb-3" style={{ color: GOLD }}>The Collection</div>
              <h2 className="font-serif-display text-4xl md:text-5xl font-light">{templates.length || 23} hand-crafted templates.</h2>
            </div>
            <Link to="/templates" className="text-xs uppercase tracking-[0.3em] hover:opacity-80" style={{ color: GOLD }}>Browse all →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {(templates.length ? templates.slice(0, 4) : Array.from({ length: 4 })).map((t, i) => (
              <Link key={i} to={t ? `/templates#${t.id}` : "/templates"} className="group block relative overflow-hidden aspect-[3/4]" style={{ background: INK_2, border: `1px solid ${LINE}` }} data-testid={`template-tile-${i}`}>
                {t?.preview && <img src={`${t.preview.split("?")[0]}?w=600&q=70&auto=format&fm=webp&fit=crop`} alt={t.name} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />}
                <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/90 to-transparent">
                  <div className="font-serif-display text-lg" style={{ color: GOLD_SOFT }}>{t?.name || ""}</div>
                  <div className="text-[10px] uppercase tracking-[0.3em] mt-1" style={{ color: GOLD }}>{t?.tagline || ""}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING — single transparent focus block */}
      <section id="pricing" className="py-20 md:py-28 px-6 md:px-10" style={{ background: INK_2, borderTop: `1px solid ${LINE}`, borderBottom: `1px solid ${LINE}` }} data-testid="pricing-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <div className="text-[10px] uppercase tracking-[0.5em] mb-3" style={{ color: GOLD }}>Simple. Transparent.</div>
            <h2 className="font-serif-display text-4xl md:text-6xl font-light leading-tight">
              Build free. <em style={{ color: GOLD }}>Pay only when</em> you&apos;re ready to go live.
            </h2>
            <p className="mt-5 text-base md:text-lg max-w-2xl mx-auto" style={{ color: "#a8a098" }}>
              No credit card to start. No monthly fees. No subscription. Try everything, preview as many times as you want — and decide later.
            </p>
          </div>

          {/* The single bold FREE block */}
          <div className="relative overflow-hidden" style={{ background: INK, border: `2px solid ${GOLD}` }} data-testid="pricing-free">
            <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(circle at 50% 0%, ${GOLD}1a 0%, transparent 55%)` }} />
            <div className="relative p-8 md:p-14 text-center">
              <div className="text-[10px] uppercase tracking-[0.5em]" style={{ color: GOLD }}>Start today · No card required</div>
              <div className="mt-5 flex items-baseline justify-center gap-3">
                <div className="font-serif-display font-light leading-none" style={{ color: GOLD_SOFT, fontSize: "clamp(72px, 14vw, 160px)" }}>FREE</div>
              </div>
              <div className="mt-2 text-sm md:text-base uppercase tracking-[0.3em]" style={{ color: "#a8a098" }}>to Build & Preview — Unlimited</div>

              <div className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-3 text-left max-w-xl mx-auto text-sm" style={{ color: GOLD_SOFT }}>
                <div>✓ Try all 23 cinematic templates</div>
                <div>✓ Customize every detail — fully</div>
                <div>✓ Live mobile + desktop preview</div>
                <div>✓ Save unlimited drafts</div>
                <div>✓ Change template anytime</div>
                <div>✓ Share private preview with family</div>
              </div>

              <Link to="/signup" data-testid="pricing-free-cta" className="mt-10 inline-flex items-center justify-center gap-2 w-full md:w-auto md:min-w-[320px] py-4 md:py-5 px-8 text-sm uppercase tracking-[0.3em] font-semibold transition-transform hover:scale-[1.02]" style={{ background: GOLD, color: INK }}>
                Start Building Free →
              </Link>
              <div className="mt-5 text-[11px] uppercase tracking-[0.4em]" style={{ color: "#807870" }}>
                ★ 30-second signup · No card · No commitment
              </div>
            </div>
          </div>

          {/* Soft disclosure — honest about publish-time pricing */}
          <div className="mt-6 p-6 md:p-7 text-center" style={{ background: INK, border: `1px solid ${LINE}` }} data-testid="pricing-disclosure">
            <div className="text-[10px] uppercase tracking-[0.4em] mb-3" style={{ color: GOLD }}>When you&apos;re ready to share with guests</div>
            <p className="text-base md:text-lg leading-relaxed max-w-2xl mx-auto" style={{ color: GOLD_SOFT }}>
              One-time publish fee unlocks your custom URL, live RSVP tracker, guest QR cards & auto-reminders.
            </p>
            <p className="mt-3 text-sm" style={{ color: "#807870" }}>
              Price shown at checkout · No subscription · One-time only · 7-day refund
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-x-8 gap-y-2 text-[10px] uppercase tracking-[0.35em]" style={{ color: "#a8a098" }}>
              <span>✓ No hidden fees</span>
              <span>✓ No monthly charges</span>
              <span>✓ You decide when</span>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — minimal, dark */}
      {testimonials.length > 0 && (
        <section className="py-20 md:py-28 px-6 md:px-10" data-testid="testimonials-section">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-[10px] uppercase tracking-[0.5em] mb-4" style={{ color: GOLD }}>Real couples</div>
            <blockquote className="font-serif-display text-2xl md:text-4xl italic font-light leading-relaxed" style={{ color: GOLD_SOFT }}>
              &ldquo;{testimonials[0].quote}&rdquo;
            </blockquote>
            <div className="mt-6 text-sm uppercase tracking-[0.3em]" style={{ color: "#a8a098" }}>
              — {testimonials[0].couple_names}{testimonials[0].location ? ` · ${testimonials[0].location}` : ""}
            </div>
          </div>
        </section>
      )}

      {/* SECONDARY CTA — Custom design */}
      <section id="schedule-meeting" className="py-20 md:py-28 px-6 md:px-10" style={{ background: INK_2, borderTop: `1px solid ${LINE}` }} data-testid="custom-cta-section">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.5em] mb-3" style={{ color: GOLD }}>★ Bespoke service</div>
            <h2 className="font-serif-display text-3xl md:text-5xl font-light">Want a <em style={{ color: GOLD }}>fully custom</em> wedding website?</h2>
            <p className="mt-5 leading-relaxed" style={{ color: "#a8a098" }}>
              Our design team builds you a one-of-a-kind website — your colors, your fonts, your custom illustrations, your story. Drop your details and we&apos;ll call you within 1 hour.
            </p>
            <div className="mt-6 space-y-2 text-sm" style={{ color: "#a8a098" }}>
              <div>📞 <a href={`tel:+91${cfg.support_phone}`} className="hover:underline" style={{ color: GOLD_SOFT }}>+91 {cfg.support_phone}</a></div>
              <div>⏱ Mon–Sat · 10am–9pm IST</div>
            </div>
          </div>
          <div className="p-6 md:p-8" style={{ background: INK, border: `1px solid ${LINE}` }}>
            <CustomLeadForm phone={cfg.support_phone} />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-12 px-6 md:px-10" style={{ background: INK, borderTop: `1px solid ${LINE}` }}>
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-4 text-xs" style={{ color: "#807870" }}>
          <div>
            <div className="font-serif-display text-lg mb-1" style={{ color: GOLD_SOFT }}>Weddinsite</div>
            <div>© {new Date().getFullYear()} · Made with ♥ in India</div>
          </div>
          <div className="flex flex-wrap gap-5 uppercase tracking-[0.25em]">
            <Link to="/templates">Templates</Link>
            <a href="#pricing">Pricing</a>
            <a href="#schedule-meeting">Custom</a>
            <Link to="/login">Sign in</Link>
            <a href={`tel:+91${cfg.support_phone}`}>+91 {cfg.support_phone}</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
